import { useState } from 'react';
import { NonEmptyArray } from 'fp-ts/NonEmptyArray';
import { ProductType, useRepositoryQuery } from '@mews-commander/data-access';
import { LocalizedProperty, lt } from '@mews/framework';
import { ModalSidePanel, SuspenseBoundary, TabbedContent } from '@mews-commander/ui';
import { createActorProfilesMapper } from '@mews-commander/utils';
import { Stack, Tag } from '@optimus-web/core';
import { AllowanceSidePanelTab } from '../../forms/types';
import { useCurrentService } from '../../queries/useCurrentService';
import { HeaderActions } from './HeaderActions';
import { GeneralTabContent } from './GeneralTabContent';
import { AllowancePricingTabContent } from './AllowancePricingTabContent';
import { AllowanceOptionsTabContent } from './AllowanceOptionsTabContent';
import { AllowanceAccountingTabContent } from './AllowanceAccountingTabContent';

interface Props {
    closeSidePanel: () => void;
    allowanceId?: string;
    productType?: ProductType;
}

const ALLOWANCE_SIDE_PANEL_TABS: NonEmptyArray<AllowanceSidePanelTab> = ['details', 'pricing', 'options', 'accounting'];

export const AllowanceSidePanel = ({ allowanceId, closeSidePanel, productType }: Props) => {
    const [currentTab, setCurrentTab] = useState<AllowanceSidePanelTab>('details');
    const { id } = useCurrentService();

    const { data, isFetching } = useRepositoryQuery(
        'allowances',
        'get',
        {
            allowanceProductId: allowanceId ?? '',
            serviceId: id,
        },
        {
            select: ({ profiles, ...allowance }) => createActorProfilesMapper(profiles)(allowance),
            enabled: !!allowanceId && productType === ProductType.Allowance,
        }
    );

    return (
        <ModalSidePanel
            content={
                data && allowanceId ? (
                    <SuspenseBoundary>
                        <TabbedContent
                            initialTab={currentTab}
                            inline
                            onTabClick={(_, value) => setCurrentTab(value as AllowanceSidePanelTab)}
                            showDivider
                            tabs={ALLOWANCE_SIDE_PANEL_TABS}
                            tabLabels={{
                                details: lt('DetailPlural'),
                                pricing: lt('Pricing'),
                                options: lt('Options'),
                                accounting: lt('Accounting'),
                            }}
                        >
                            {({ matchTabs }) =>
                                matchTabs({
                                    details: () => (
                                        <GeneralTabContent
                                            createdUtc={data.createdUtc}
                                            creatorProfile={data.creatorProfile}
                                            descriptions={data.descriptions}
                                            externalIdentifier={data.externalIdentifier}
                                            externalNames={data.externalNames}
                                            imageId={data.imageId}
                                            names={data.names}
                                            ordering={data.ordering}
                                            shortNames={data.shortNames}
                                            updatedUtc={data.updatedUtc}
                                            updaterProfile={data.updaterProfile}
                                        />
                                    ),
                                    pricing: () => <AllowancePricingTabContent allowanceId={allowanceId} />,
                                    options: () => <AllowanceOptionsTabContent allowanceId={allowanceId} />,
                                    accounting: () => <AllowanceAccountingTabContent allowanceId={allowanceId} />,
                                })
                            }
                        </TabbedContent>
                    </SuspenseBoundary>
                ) : null
            }
            headerActions={
                allowanceId ? (
                    <HeaderActions
                        closeSidePanel={closeSidePanel}
                        currentTab={currentTab}
                        productId={allowanceId}
                        productType={ProductType.Allowance}
                    />
                ) : null
            }
            loading={isFetching}
            onClose={() => {
                closeSidePanel();
                setCurrentTab('details');
            }}
            opened={!!allowanceId && productType === ProductType.Allowance}
            title={
                data ? (
                    <Stack itemAlignment="center" spacing="150">
                        <LocalizedProperty property={data.names} />
                        <Tag variant="lime">{lt('Allowance')}</Tag>
                    </Stack>
                ) : null
            }
            width="medium"
        />
    );
};
